import React, { useState, useEffect } from 'react';
import Icon from '../AppIcon';


const RazorpayDebugPanel = ({ isOpen, onClose }) => {
  const [logs, setLogs] = useState([]);
  const [filter, setFilter] = useState('all'); // all, success, error, warning, info
  const [isMinimized, setIsMinimized] = useState(false);

  useEffect(() => {
    if (!isOpen) return;

    // Intercept console methods
    const originalConsoleLog = console.log;
    const originalConsoleError = console.error;
    const originalConsoleWarn = console.warn;
    const originalConsoleInfo = console.info;

    const addLog = (type, args) => {
      const timestamp = new Date()?.toISOString();
      const message = args?.map(arg => {
        if (typeof arg === 'object') {
          try {
            return JSON.stringify(arg, null, 2);
          } catch (e) {
            return String(arg);
          }
        }
        return String(arg);
      })?.join(' ');

      setLogs(prev => [{
        id: Date.now() + Math.random(),
        timestamp,
        type,
        message,
        raw: args
      }, ...prev]?.slice(0, 100)); // Keep last 100 logs
    };

    console.log = (...args) => {
      originalConsoleLog(...args);
      addLog('info', args);
    };

    console.error = (...args) => {
      originalConsoleError(...args);
      addLog('error', args);
    };

    console.warn = (...args) => {
      originalConsoleWarn(...args);
      addLog('warning', args);
    };

    console.info = (...args) => {
      originalConsoleInfo(...args);
      addLog('info', args);
    };

    // Listen for Razorpay-specific window messages
    const handleMessage = (event) => {
      if (event?.data?.source === 'frame' || event?.data?.source === 'checkoutjs') {
        addLog('info', ['[Razorpay Message]', event?.data]);
      }
    };

    window.addEventListener('message', handleMessage);

    // Cleanup
    return () => {
      console.log = originalConsoleLog;
      console.error = originalConsoleError;
      console.warn = originalConsoleWarn;
      console.info = originalConsoleInfo;
      window.removeEventListener('message', handleMessage);
    };
  }, [isOpen]);

  const clearLogs = () => {
    setLogs([]);
  };

  const exportLogs = () => {
    const logData = logs?.map(log => ({
      timestamp: log?.timestamp,
      type: log?.type,
      message: log?.message
    }));
    const blob = new Blob([JSON.stringify(logData, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `razorpay-debug-${Date.now()}.json`;
    a?.click();
    URL.revokeObjectURL(url);
  };

  const filteredLogs = logs?.filter(log => {
    if (filter === 'all') return true;
    return log?.type === filter;
  });

  const getLogIcon = (type) => {
    switch (type) {
      case 'error': return { name: 'XCircle', color: '#ef4444' };
      case 'warning': return { name: 'AlertTriangle', color: '#f59e0b' };
      case 'success': return { name: 'CheckCircle2', color: '#10b981' };
      default: return { name: 'Info', color: '#3b82f6' };
    }
  };

  const getLogBgColor = (type) => {
    switch (type) {
      case 'error': return 'bg-red-50 border-red-200';
      case 'warning': return 'bg-yellow-50 border-yellow-200';
      case 'success': return 'bg-green-50 border-green-200';
      default: return 'bg-blue-50 border-blue-200';
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[9999] pointer-events-none">
      <div 
        className={`absolute bottom-4 right-4 bg-white rounded-lg shadow-2xl border-2 border-primary pointer-events-auto transition-all duration-300 ${
          isMinimized ? 'w-80 h-14' : 'w-[600px] h-[600px]'
        }`}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-3 border-b border-border bg-primary/5">
          <div className="flex items-center gap-2">
            <Icon name="Bug" size={20} color="var(--color-primary)" />
            <h3 className="font-semibold text-foreground text-sm">
              Razorpay Debug Console
            </h3>
            <span className="text-xs text-muted-foreground">({logs?.length} logs)</span>
          </div>
          <div className="flex items-center gap-1">
            <button
              onClick={() => setIsMinimized(!isMinimized)}
              className="p-1.5 hover:bg-muted rounded transition-colors"
              title={isMinimized ? 'Maximize' : 'Minimize'}
            >
              <Icon 
                name={isMinimized ? 'Maximize2' : 'Minimize2'} 
                size={16} 
                color="var(--color-muted-foreground)" 
              />
            </button>
            <button
              onClick={onClose}
              className="p-1.5 hover:bg-muted rounded transition-colors"
              title="Close"
            >
              <Icon name="X" size={16} color="var(--color-muted-foreground)" />
            </button>
          </div>
        </div>

        {!isMinimized && (
          <>
            {/* Toolbar */}
            <div className="flex items-center justify-between p-2 border-b border-border bg-muted/30">
              <div className="flex items-center gap-1">
                <button
                  onClick={() => setFilter('all')}
                  className={`px-2 py-1 text-xs rounded transition-colors ${
                    filter === 'all' ? 'bg-primary text-white' : 'bg-white text-muted-foreground hover:bg-muted'
                  }`}
                >
                  All
                </button>
                <button
                  onClick={() => setFilter('error')}
                  className={`px-2 py-1 text-xs rounded transition-colors ${
                    filter === 'error' ? 'bg-red-500 text-white' : 'bg-white text-muted-foreground hover:bg-muted'
                  }`}
                >
                  Errors
                </button>
                <button
                  onClick={() => setFilter('warning')}
                  className={`px-2 py-1 text-xs rounded transition-colors ${
                    filter === 'warning' ? 'bg-yellow-500 text-white' : 'bg-white text-muted-foreground hover:bg-muted'
                  }`}
                >
                  Warnings
                </button>
                <button
                  onClick={() => setFilter('info')}
                  className={`px-2 py-1 text-xs rounded transition-colors ${
                    filter === 'info' ? 'bg-blue-500 text-white' : 'bg-white text-muted-foreground hover:bg-muted'
                  }`}
                >
                  Info
                </button>
              </div>
              <div className="flex items-center gap-1">
                <button
                  onClick={exportLogs}
                  className="px-2 py-1 text-xs bg-white text-muted-foreground hover:bg-muted rounded transition-colors flex items-center gap-1"
                  title="Export logs as JSON"
                >
                  <Icon name="Download" size={12} color="var(--color-muted-foreground)" />
                  Export
                </button>
                <button
                  onClick={clearLogs}
                  className="px-2 py-1 text-xs bg-white text-muted-foreground hover:bg-muted rounded transition-colors flex items-center gap-1"
                  title="Clear all logs"
                >
                  <Icon name="Trash2" size={12} color="var(--color-muted-foreground)" />
                  Clear
                </button>
              </div>
            </div>

            {/* Logs Container */}
            <div className="h-[calc(100%-100px)] overflow-y-auto p-2 space-y-2 bg-gray-50">
              {filteredLogs?.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-full text-center">
                  <Icon name="FileText" size={48} color="var(--color-muted-foreground)" className="mb-2 opacity-50" />
                  <p className="text-sm text-muted-foreground">
                    {filter === 'all' ? 'No logs yet. Trigger a payment to see debug information.' : `No ${filter} logs found.`}
                  </p>
                </div>
              ) : (
                filteredLogs?.map(log => {
                  const icon = getLogIcon(log?.type);
                  return (
                    <div 
                      key={log?.id} 
                      className={`p-2 rounded border ${getLogBgColor(log?.type)} text-xs`}
                    >
                      <div className="flex items-start gap-2">
                        <Icon name={icon?.name} size={14} color={icon?.color} className="flex-shrink-0 mt-0.5" />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between mb-1">
                            <span className="font-mono text-[10px] text-muted-foreground">
                              {new Date(log.timestamp)?.toLocaleTimeString()}.{new Date(log.timestamp)?.getMilliseconds()}
                            </span>
                            <span className={`text-[10px] font-semibold uppercase ${
                              log?.type === 'error' ? 'text-red-600' :
                              log?.type === 'warning' ? 'text-yellow-600' :
                              log?.type === 'success'? 'text-green-600' : 'text-blue-600'
                            }`}>
                              {log?.type}
                            </span>
                          </div>
                          <pre className="whitespace-pre-wrap break-words font-mono text-[11px] text-foreground">
                            {log?.message}
                          </pre>
                        </div>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default RazorpayDebugPanel;